import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconButton } from '@calendly/ui/components/icon-button';
import { AiNotetakerIcon, CloseIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { Modal } from '@calendly/ui/components/modal';
import { SwitchInput } from '@calendly/ui/components/switch-input';
import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { errorColor } from '@calendly/ui/theme';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RecapImage, RecapShapesImage } from 'features/billing';
import { EventTypeSelect, RecordingRulesProvider, useRecordingRulesContext, useGetEventsQuery, useGetMeetingIntelligenceUserSettingsQuery, useGetUserCalendarsQuery, useLazyGetMoreEventsQuery, useUpdateUserMeetingIntelligenceDataMutation, MeetingIntelligenceProvider, useMeetingIntelligenceContext } from 'features/meeting_intelligence';
import { TRACKING_EVENT_NAMES, useTrack, useTrackOnce } from 'hooks/analytics_events';
import { useAddTrackedExternalMeetingMutation, useAddTrackedMeetingMutation, useDestroyTrackedMeetingMutation, useRemoveTrackedMeetingBotMutation } from 'hooks/tracked_meetings/api';
import { timeNotationSelector, timezoneSelector } from 'selectors/current_user';
import { isEventInProgress, isEventInTheFuture } from 'shared/utils/get_badge_key';
import { summarizeInterval } from 'shared/utils/meeting_helpers';
import { useGetNotetakerToggleState } from './hooks/use_get_notetaker_toggle_state';
const modalContentIsPhoneCss = "m1w1gju9";
const modalContentCss = "m1mf1dj0";
const layoutIsPhoneCss = "l1vymiwf";
const layoutCss = "l1tnkq9n";
const leftColumnCss = "l1v6wxjz";
const leftColumnIsPhoneCss = "l22wshn";
const rightColumnCss = "rcv1avk";
const closeButtonCss = "c1rewt8b";
const meetingCardCss = "mg6i48z";
const meetingDetailsCss = "mozgepw";
const statusDotCss = "soj3wbe";
const meetingTitleCss = "mw81bl0";
const toggleWithTextCss = "t1tawcwf";
const sectionTitleCss = "sundt6l";
const optionCardCss = "o1fjgctl";
const optionDetailsCss = "oh4wwsk";
const selectWrapperCss = "s3tc9t2";
const selectErrorCss = "s2fe172";
const footerNoteCss = "flxurji";
const settingsLoadingCss = "solw4g0";
const saveButtonCss = "s1j62dkn";
const rightImageCss = "r1dgejqk";
const rightShapesCss = "r1qeeg1z";
const ModalContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(modalContentCss, {
    [modalContentIsPhoneCss]: isPhone
  }, props.className)} />;
});
const NotetakerSettingsActionsContext = createContext(null);
const useNotetakerSettingsActions = () => useContext(NotetakerSettingsActionsContext);
const NotetakerSettingsSection = () => {
  const [updateSettings, {
    isLoading: isSaving
  }] = useUpdateUserMeetingIntelligenceDataMutation();
  const {
    calendlyMeetings,
    setCalendlyMeetings,
    connectedNonCalendlyMeetings,
    setConnectedNonCalendlyMeetings,
    isValidCalendar,
    email,
    canDisableMeetingRecaps,
    handleSubmit
  } = useMeetingIntelligenceContext();
  const {
    eventTypes,
    hasEventError,
    actualSelectedCount,
    handleEventTypeSelect,
    handleSelectAll,
    handleClearAll,
    handleFetchMoreEvents,
    handleSubmitRecordingRules
  } = useRecordingRulesContext();
  const {
    isMeetingToggleDirty,
    isSettingsDirty,
    onClose,
    onSaveMeetingToggle,
    setIsDirty
  } = useNotetakerSettingsActions();
  const track = useTrack();
  const {
    pathname
  } = useLocation();
  const canEditSettings = Boolean(canDisableMeetingRecaps);
  const calendlyMeetingsChecked = Boolean(calendlyMeetings);
  const connectedNonCalendlyMeetingsChecked = Boolean(connectedNonCalendlyMeetings);
  const handleSave = useCallback(async () => {
    if (!isSettingsDirty && !isMeetingToggleDirty) {
      onClose();
      return;
    }
    if (isMeetingToggleDirty) {
      await onSaveMeetingToggle();
    }
    if (isSettingsDirty) {
      const selectedEventTypes = handleSubmitRecordingRules();
      if (!selectedEventTypes) {
        return;
      }
      const values = handleSubmit();
      const result = await updateSettings({
        autoRecordV3Calendly: values.calendlyMeetings,
        autoRecordV3NonCalendly: values.connectedNonCalendlyMeetings,
        autoRecordV3NonCalendlyHostedMeetingsOnly: values.recordHostedMeetings,
        autoRecordV3RequireExternalInvitees: values.externalInviteeMeetings,
        autoShareRecapEmails: values.autoShareRecapEmails,
        autoRecordV3CalendlyIncludeList: selectedEventTypes
      });
      if (result?.error) {
        return;
      }
    }
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'saving',
        modal_name: 'add_notetaker_modal',
        location: 'meetings_page',
        path: pathname
      }
    });
    onClose();
  }, [handleSubmit, handleSubmitRecordingRules, isMeetingToggleDirty, isSettingsDirty, onClose, onSaveMeetingToggle, pathname, track, updateSettings]);
  return <>
      <div className={optionCardCss}>
        <div className={optionDetailsCss}>
          <BodyText weight="bold">{I18n.t('publisher.meeting_intelligence.add_notetaker_modal.settings.calendly_meetings_title')}</BodyText>
          {calendlyMeetingsChecked && <div className={selectWrapperCss}>
              <EventTypeSelect placeholder={I18n.t('publisher.meeting_intelligence.add_notetaker_modal.settings.event_type_placeholder')} disabled={!canEditSettings} eventTypes={eventTypes} onChange={handleEventTypeSelect} handleSelectAll={handleSelectAll} handleClearAll={handleClearAll} handleFetchMoreEvents={handleFetchMoreEvents} actualSelectedCount={actualSelectedCount} isWide />
              {hasEventError && <BodyText size="small" color={errorColor} className={selectErrorCss}>
                  {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.dropdown_error_text')}
                </BodyText>}
            </div>}
        </div>
        <label>
          <SwitchInput checked={calendlyMeetingsChecked} onChange={() => {
          setIsDirty(true);
          setCalendlyMeetings(!calendlyMeetings);
        }} role="switch" disabled={!canEditSettings || isSaving} />
        </label>
      </div>
      <div className={optionCardCss}>
        <div className={optionDetailsCss}>
          <BodyText weight="bold">
            {I18n.t('publisher.meeting_intelligence.add_notetaker_modal.settings.connected_calendar_title')}
          </BodyText>
          <BodyText size="small" color="muted">
            {email ? I18n.t('publisher.meeting_intelligence.add_notetaker_modal.settings.connected_calendar_label', {
            email
          }) : I18n.t('publisher.meeting_intelligence.add_notetaker_modal.settings.connected_calendar_placeholder')}
          </BodyText>
        </div>
        <label>
          <SwitchInput checked={connectedNonCalendlyMeetingsChecked} onChange={() => {
          setIsDirty(true);
          setConnectedNonCalendlyMeetings(!connectedNonCalendlyMeetings);
        }} role="switch" disabled={!canEditSettings || isSaving || !isValidCalendar} />
        </label>
      </div>
      <BodyText size="small" className={footerNoteCss}>
        {I18n.t('publisher.meeting_intelligence.add_notetaker_modal.settings.footer_note')}
      </BodyText>
      <Button onClick={handleSave} className={saveButtonCss} loading={isSaving} disabled={!canEditSettings || isSaving}>
        {I18n.t('publisher.meeting_intelligence.add_notetaker_modal.settings.save_button')}
      </Button>
    </>;
};
const NotetakerSettingsContent = ({
  isOpen,
  settingsState,
  fetchMoreEvents,
  settingsActions
}) => {
  const {
    isSettingsLoading,
    meetingIntelligenceData,
    calendarData,
    eventTypeData
  } = settingsState;
  const isSettingsReady = isOpen && !isSettingsLoading && meetingIntelligenceData !== undefined && calendarData !== undefined && eventTypeData !== undefined;
  if (isOpen && isSettingsLoading) {
    return <div className={settingsLoadingCss}>
        <Loader size="small" />
      </div>;
  }
  if (!isSettingsReady) {
    return null;
  }
  return <NotetakerSettingsActionsContext.Provider value={settingsActions}>
      <RecordingRulesProvider isLoading={isSettingsLoading} miData={meetingIntelligenceData} calendarData={calendarData} eventTypeData={eventTypeData} fetchMoreEvents={fetchMoreEvents} handleSetIsDirty={settingsActions.setIsDirty}>
        <MeetingIntelligenceProvider isLoading={isSettingsLoading} meetingIntelligenceData={meetingIntelligenceData} calendarData={calendarData}>
          <NotetakerSettingsSection />
        </MeetingIntelligenceProvider>
      </RecordingRulesProvider>
    </NotetakerSettingsActionsContext.Provider>;
};
export const AddNotetakerModal = ({
  isOpen,
  onClose,
  onMeetingUpdated,
  selectedMeeting
}) => {
  const isPhone = useDeviceType('isPhone');
  const {
    pathname
  } = useLocation();
  const track = useTrack();
  const timezone = useSelector(timezoneSelector);
  const timeNotation = useSelector(timeNotationSelector);
  const toggleImprovementsEnabled = useFlag('notetaker_ux_improvements');
  const [isMeetingToggleDirty, setIsMeetingToggleDirty] = useState(false);
  const [isSettingsDirty, setIsSettingsDirty] = useState(false);
  const [pendingNotetakerEnabled, setPendingNotetakerEnabled] = useState(null);
  const autoAddRef = useRef(null);
  const miEnableRef = useRef(false);
  const {
    data: meetingIntelligenceData,
    isLoading: isMeetingIntelligenceLoading
  } = useGetMeetingIntelligenceUserSettingsQuery({}, {
    skip: !isOpen
  });
  const [updateMiEnabled] = useUpdateUserMeetingIntelligenceDataMutation();
  const {
    data: calendarData,
    isLoading: isCalendarDataLoading
  } = useGetUserCalendarsQuery({}, {
    skip: !isOpen
  });
  const {
    data: eventTypeData,
    isLoading: isEventDataLoading
  } = useGetEventsQuery({}, {
    skip: !isOpen
  });
  const [getMoreEventsTrigger] = useLazyGetMoreEventsQuery();
  const isSettingsLoading = isMeetingIntelligenceLoading || isCalendarDataLoading || isEventDataLoading;
  const meetingDisplay = useMemo(() => {
    if (!selectedMeeting?.startTime || !selectedMeeting?.endTime) {
      return {
        title: selectedMeeting?.name || selectedMeeting?.eventType?.name || I18n.t('publisher.meeting_intelligence.add_notetaker_modal.meeting_details.unavailable_title'),
        time: I18n.t('publisher.meeting_intelligence.add_notetaker_modal.meeting_details.unavailable_time')
      };
    }
    const start = moment(selectedMeeting.startTime).tz(timezone);
    const end = moment(selectedMeeting.endTime).tz(timezone);
    return {
      title: selectedMeeting?.name || selectedMeeting?.eventType?.name || I18n.t('publisher.meeting_intelligence.add_notetaker_modal.meeting_details.default_title'),
      time: `${start.format('ddd, MMMM D')} (${summarizeInterval({
        from: start,
        to: end
      }, timeNotation, false)})`
    };
  }, [selectedMeeting, timeNotation, timezone]);
  const {
    showNotetaker,
    notetakerAdded,
    trackedMeetingUuid,
    unsupportedLocation,
    isLoading: isNotetakerStateLoading,
    isRecapProcessingOrCompleted
  } = useGetNotetakerToggleState(selectedMeeting);
  const [addTrackedMeeting, addTrackedMeetingState] = useAddTrackedMeetingMutation();
  const [addExternalTrackedMeeting, addExternalTrackedMeetingState] = useAddTrackedExternalMeetingMutation();
  const [removeTrackedMeeting, removeTrackedMeetingState] = useDestroyTrackedMeetingMutation();
  const [removeTrackedMeetingBot, removeTrackedMeetingBotState] = useRemoveTrackedMeetingBotMutation();
  const trackedMeetingMutation = useMemo(() => selectedMeeting?.kind && selectedMeeting.kind !== 'CALENDLY' ? {
    state: addExternalTrackedMeetingState,
    fn: addExternalTrackedMeeting
  } : {
    state: addTrackedMeetingState,
    fn: addTrackedMeeting
  }, [addExternalTrackedMeeting, addExternalTrackedMeetingState, addTrackedMeeting, addTrackedMeetingState, selectedMeeting?.kind]);
  const isMeetingInProgress = selectedMeeting ? isEventInProgress(selectedMeeting) : false;
  const dontAllowNotetakerRemoval = !toggleImprovementsEnabled && isMeetingInProgress && notetakerAdded;
  const isNotetakerActionLoading = isNotetakerStateLoading || trackedMeetingMutation.state.isLoading || removeTrackedMeetingState.isLoading || removeTrackedMeetingBotState.isLoading;
  const isNotetakerEnabled = Boolean(pendingNotetakerEnabled ?? notetakerAdded);
  const toggleDisabled = !selectedMeeting || !showNotetaker || unsupportedLocation || isRecapProcessingOrCompleted || dontAllowNotetakerRemoval || isNotetakerActionLoading;
  const handleToggleNotetaker = () => {
    if (toggleDisabled || !selectedMeeting) {
      return;
    }
    setIsMeetingToggleDirty(true);
    setPendingNotetakerEnabled(!isNotetakerEnabled);
  };
  const handleFetchMoreEvents = useCallback(async groupData => {
    const {
      id: groupId,
      type: groupType,
      currentPage = 1
    } = groupData;
    const nextPage = currentPage + 1;
    const {
      data: moreData,
      isLoading: moreDataLoading,
      isSuccess
    } = await getMoreEventsTrigger({
      groupId,
      groupType,
      page: nextPage
    });
    return {
      moreData,
      moreDataLoading,
      isSuccess
    };
  }, [getMoreEventsTrigger]);
  const handleSaveMeetingToggle = useCallback(async (nextEnabled, options = {}) => {
    const {
      skipRefresh = false
    } = options;
    if (!selectedMeeting) {
      return false;
    }
    const targetEnabled = nextEnabled ?? pendingNotetakerEnabled ?? notetakerAdded;
    const shouldUpdate = targetEnabled !== notetakerAdded;
    if (!shouldUpdate) {
      setIsMeetingToggleDirty(false);
      return false;
    }
    if (targetEnabled) {
      const result = await trackedMeetingMutation.fn({
        eventId: selectedMeeting.uuid,
        eventUuid: selectedMeeting.uuid,
        event: selectedMeeting
      });
      if (result?.error) {
        setPendingNotetakerEnabled(null);
        setIsMeetingToggleDirty(false);
        return false;
      }
    } else {
      if (!trackedMeetingUuid) {
        return false;
      }
      if (!isEventInTheFuture(selectedMeeting) && toggleImprovementsEnabled) {
        const result = await removeTrackedMeetingBot({
          meetingUuid: trackedMeetingUuid,
          foreignEventId: selectedMeeting.uuid
        });
        if (result?.error) {
          return false;
        }
      } else {
        const result = await removeTrackedMeeting({
          meetingUuid: trackedMeetingUuid,
          foreignEventId: selectedMeeting.uuid
        });
        if (result?.error) {
          return false;
        }
      }
    }
    setIsMeetingToggleDirty(false);
    if (!skipRefresh) {
      onMeetingUpdated?.(selectedMeeting.uuid);
    }
    return true;
  }, [notetakerAdded, onMeetingUpdated, pendingNotetakerEnabled, removeTrackedMeeting, removeTrackedMeetingBot, selectedMeeting, toggleImprovementsEnabled, trackedMeetingMutation, trackedMeetingUuid]);
  useEffect(() => {
    if (!isOpen) {
      autoAddRef.current = null;
      miEnableRef.current = false;
      setIsMeetingToggleDirty(false);
      setIsSettingsDirty(false);
      setPendingNotetakerEnabled(null);
    }
  }, [isOpen]);

  // Auto-enable meeting intelligence if disabled
  useEffect(() => {
    if (!isOpen || miEnableRef.current || !meetingIntelligenceData) {
      return;
    }
    if (meetingIntelligenceData.adminDisabledNotetaker) {
      miEnableRef.current = true;
      return;
    }
    if (meetingIntelligenceData.miEnabled === false) {
      miEnableRef.current = true;
      updateMiEnabled({
        miEnabled: true
      });
      return;
    }
    miEnableRef.current = true;
  }, [updateMiEnabled, isOpen, meetingIntelligenceData]);

  // Auto-add notetaker to the selected meeting if eligible
  useEffect(() => {
    if (!isOpen || !selectedMeeting || unsupportedLocation || isRecapProcessingOrCompleted || notetakerAdded || isNotetakerStateLoading || !showNotetaker || !meetingIntelligenceData?.miEnabled) {
      return;
    }
    if (autoAddRef.current === selectedMeeting.uuid) {
      return;
    }
    autoAddRef.current = selectedMeeting.uuid;
    setPendingNotetakerEnabled(true);
    setIsMeetingToggleDirty(true);
    const autoAddNotetaker = async () => {
      const didUpdate = await handleSaveMeetingToggle(true, {
        skipRefresh: true
      });
      if (didUpdate) {
        onMeetingUpdated?.(selectedMeeting.uuid);
      }
    };
    autoAddNotetaker();
  }, [handleSaveMeetingToggle, isOpen, isRecapProcessingOrCompleted, notetakerAdded, isNotetakerStateLoading, showNotetaker, meetingIntelligenceData?.miEnabled, selectedMeeting, unsupportedLocation, onMeetingUpdated]);
  const settingsState = useMemo(() => ({
    isSettingsLoading,
    meetingIntelligenceData,
    calendarData,
    eventTypeData
  }), [isSettingsLoading, meetingIntelligenceData, calendarData, eventTypeData]);
  const settingsActions = useMemo(() => ({
    isMeetingToggleDirty,
    isSettingsDirty,
    onClose,
    onSaveMeetingToggle: handleSaveMeetingToggle,
    setIsDirty: setIsSettingsDirty
  }), [handleSaveMeetingToggle, isMeetingToggleDirty, isSettingsDirty, onClose]);
  useTrackOnce(isOpen, {
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      action: 'opening',
      modal_name: 'add_notetaker_modal',
      location: 'meetings_page',
      path: pathname
    }
  });
  const handleClose = useCallback(() => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'closing',
        modal_name: 'add_notetaker_modal',
        location: 'meetings_page',
        path: pathname
      }
    });
    onClose();
  }, [onClose, pathname, track]);
  if (isOpen && !selectedMeeting) {
    return null;
  }
  return <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <IconButton aria-label={I18n.t('publisher.meeting_intelligence.add_notetaker_modal.close_label')} onClick={handleClose} className={closeButtonCss}>
          <CloseIcon />
        </IconButton>
        <div className={classNames(layoutCss, {
        [layoutIsPhoneCss]: isPhone
      })}>
          <div className={classNames(leftColumnCss, {
          [leftColumnIsPhoneCss]: isPhone
        })}>
            <div>
              <HeadingText level={2}>
                {I18n.t('publisher.meeting_intelligence.add_notetaker_modal.header.title')}
              </HeadingText>
              <BodyText>
                {I18n.t('publisher.meeting_intelligence.add_notetaker_modal.header.subtitle')}
              </BodyText>
            </div>
            <div className={meetingCardCss}>
              <div className={meetingDetailsCss}>
                <span className={statusDotCss} />
                <div>
                  <BodyText className={meetingTitleCss}>
                    {meetingDisplay.title}
                  </BodyText>
                  <BodyText size="small" color="muted">
                    {meetingDisplay.time}
                  </BodyText>
                </div>
              </div>
              <div className={toggleWithTextCss}>
                <BodyText size="small">
                  {isNotetakerEnabled ? I18n.t('publisher.meeting_intelligence.add_notetaker_modal.toggle.on') : I18n.t('publisher.meeting_intelligence.add_notetaker_modal.toggle.off')}
                </BodyText>
                <AiNotetakerIcon />
                <label>
                  <SwitchInput checked={isNotetakerEnabled} onChange={handleToggleNotetaker} role="switch" disabled={toggleDisabled} />
                </label>
              </div>
            </div>
            <div>
              <BodyText className={sectionTitleCss}>
                {I18n.t('publisher.meeting_intelligence.add_notetaker_modal.settings.title')}
              </BodyText>
            </div>
            <NotetakerSettingsContent isOpen={isOpen} settingsState={settingsState} fetchMoreEvents={handleFetchMoreEvents} settingsActions={settingsActions} />
          </div>
          {!isPhone && <div className={rightColumnCss}>
              <img src={RecapImage} alt="" className={rightImageCss} />
              <img src={RecapShapesImage} alt="" className={rightShapesCss} />
            </div>}
        </div>
      </ModalContent>
    </Modal>;
};

require("./add_notetaker_modal.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./add_notetaker_modal.js");